import * as React from 'react';
import { useHistory } from 'react-router';
import axios, { CancelTokenSource } from 'axios';
import clsx from 'clsx';
import themePrimary from '../../styles/themePrimary';
import { CheckOutStateContext } from '../../providers/CheckOutStateContextProvider';
import { Typography, makeStyles, useMediaQuery } from '@material-ui/core';
import { GetCheckResponse } from '../../models/checkout/GetCheckResponse';
import SplitButtons, { SplitButtonProps } from '../common/Buttons/SplitButtons';
import RefreshButton from '../common/Buttons/RefreshButton';
import BackButton from '../common/Buttons/BackButton';
import { GetCheckRequest } from '../../models/checkout/GetCheckRequest';
import { String_System_GeneralError, String_System_ConnectionError, String_Check_SplitButton, 
    String_Check_PayCheckButton, String_Check_CheckNumberLabel, 
    String_CheckPaid, 
    String_Check_CheckDetails} from '../../utilities/StringHelper';
import ErrorDialog, { ErrorDialogProps } from '../common/ErrorDialog';
import Loader from '../common/Loader';
import { ElementDimensions } from '../../styles/ElementDimensions';
import { Skin } from '../../models/common/Skin';
import { UserStateContext } from '../../providers/UserStateContextProvider';
import { CheckState, CheckStateContext } from '../../providers/CheckStateContextProvider';
import { CheckSummary } from '../../models/common/CheckSummary';
import { CheckFlat } from '../../models/common/CheckFlat';
import CheckDetails from '../common/CheckDetails';
import { CheckState as CheckStateEnum } from '../../models/common/CheckState';
import { CheckOutType } from '../../models/common/CheckOutType';
import ShareLink from '../common/ShareLink';
import SignalR, { CheckMessageType, CheckNotificationType } from '../common/SignalR';
import { SplitType } from '../../models/common/SplitType';
import { ErrorCode } from '../../models/common/ErrorCode';
import FullWidthButton, { FullWidthButtonProps, FullWidthButtonSize } from '../common/Buttons/FullWidthButton';
import { CheckItemFlat } from '../../models/common/CheckItemFlat';
import { loadDemoCheckData } from '../../models/common/DemoData';
import { FeedbackType } from '../../models/common/FeedbackType';

const useStyles = makeStyles({
    checkDetailsContainer: {
        backgroundColor: themePrimary.palette.grey[100],
        borderRadius: '6px',
        marginLeft: ElementDimensions.margin10 + 'px',
        marginRight: ElementDimensions.margin10 + 'px',
        marginBottom: ElementDimensions.margin10 + 'px',
    },
    details: {
        position: 'absolute',
        top: (ElementDimensions.appBarHeight) + 'px',
        bottom: ElementDimensions.footerBarHeight + 'px',
        left: 0,
        right: 0,
        width: '100%',
        overflowY: 'scroll',
        overflowX: 'hidden',
    },
    detailsLandscape: {
        position: 'absolute',
        top: '0px',
        bottom: ElementDimensions.footerBarHeightLandscape + 'px',
        left: 'calc(100vw *.3)',
        width: 'calc(100vw *.7)',
        overflowY: 'scroll',
        overflowX: 'hidden',
    },
    footerButtons: {
        position: 'absolute',
        bottom: 0,
        right: 0,
        height: ElementDimensions.footerBarHeight + 'px',
        padding: ElementDimensions.padding10 + 'px ' + '0px ' + ElementDimensions.padding10 + 'px 0px',
        width: '100%',
        borderTop: '2px solid ' + themePrimary.palette.grey[200],
        backgroundColor: themePrimary.palette.primary.contrastText,
    },
    footerButtonsLandscape: {
        position: 'absolute',
        bottom: 0,
        right: 0,
        left: 'calc(100vw *.3)',
        height: ElementDimensions.footerBarHeightLandscape + 'px',
        padding: ElementDimensions.padding10 + 'px ' + '0px ' + ElementDimensions.padding10 + 'px 0px',
        width: 'calc(100vw * .7)',
        borderTop: '2px solid ' + themePrimary.palette.grey[200],
        backgroundColor: themePrimary.palette.primary.contrastText,
    },
    headerContainer: {
        padding: ElementDimensions.padding10 + 'px ' + ElementDimensions.padding10 + 'px ' + ElementDimensions.padding5 + 'px ' + ElementDimensions.padding10 + 'px',
        float: 'left',
        width: '100%',
    },
    loadingContainer: {
        margin: ElementDimensions.margin40,
        textAlign: 'center',
    },
    root: {
        height: '100%',
        overflow: 'hidden',
    },
    rootLandscape: {
        marginTop: '0',
    },    
    titleInnerContainer: {
        width: '100%',
        height: '86px',
        padding: ElementDimensions.padding10 + 'px',
        paddingTop: ElementDimensions.padding15 + 'px',
        paddingBottom: ElementDimensions.padding20 + 'px',
        display: 'flex',
    },
    titleRefreshButtonContainer: {
        margin: 'auto',
    },
    titleText: {
        color: themePrimary.palette.primary.contrastText,
        lineHeight: '23px !important',
        display: 'flex',
    	flexDirection: 'row',
    	alignItems: 'center',
        backgroundColor: props => (props as Skin).primary_main_color,
        borderRadius: '46px',
        height: '46px',
        paddingRight: ElementDimensions.padding20 + 'px',
        paddingLeft: ElementDimensions.padding20 + 'px',
    },
    titleTextContainer: {
        margin: 'auto',
        paddingLeft: '10px',
        paddingRight: '10px',
    },
    titleBackContainer: {
        margin: 'auto',
        width: '60px',
    },
});

const Check = () => {
    const isPortrait = true;
    let history = useHistory();
    const [checkSummary, setCheckSummary] = React.useState<CheckSummary>(history.location.state.checkSummary);
    const [check, setCheck] = React.useState<CheckFlat | null>(null);
    const [errorDialogOpen, setErrorDialogOpen] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string>('');
    const [errorDialogTryAgain, setErrorDialogTryAgain] = React.useState<boolean>(true);
    const [checkPaidError, setCheckPaidError] = React.useState<boolean>(false);
    const [loadError, setLoadError] = React.useState<boolean>(false);
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [isRefreshing, setIsRefreshing] = React.useState<boolean>(false);    
    const [noUnclaimedAmountsError, setNoUnclaimedAmountsError] = React.useState<boolean>(false);
    const detailsEl = React.useRef(null);
    const userState = React.useContext(UserStateContext);
    const checkOutState = React.useContext(CheckOutStateContext);
    const checkState = React.useContext(CheckStateContext);
    const classes = useStyles({ primary_main_color: checkOutState.state.skin.primary_main_color, 
        primary_light_color: checkOutState.state.skin.primary_light_color, primary_dark_color: checkOutState.state.skin.primary_dark_color });

    const signalRNotificationReceivedHandler = (type: CheckNotificationType, amount: number) => {
        if (type === CheckNotificationType.AmountClaimed) {
            loadData(axios.CancelToken.source(), false);
        }
        else if (type === CheckNotificationType.PaymentCompleted) {
            loadData(axios.CancelToken.source(), false);
        }
        else if (type === CheckNotificationType.CheckClosed) {
            loadData(axios.CancelToken.source(), false);
        }
        else if (type === CheckNotificationType.ClaimExpired) {
            loadData(axios.CancelToken.source(), false);
        }     
    }
    
    React.useEffect(() => {
        const cancelToken = axios.CancelToken;
        const source = cancelToken.source();
        
        const newCheckState: CheckState = checkState.state;
        newCheckState.paymentId = null;                    
        checkState.setState(newCheckState);

        if (checkOutState.state.checkOutType === CheckOutType.Demo) {
            loadDemoData();
        }
        else {
            loadData(source, true); 
        }             
                
        return () => {
            source.cancel();
        };
    }, []);

    const loadData = async (cancelTokenSource: CancelTokenSource, releaseClaims: boolean) => {
        setLoadError(false);
        setLoaded(false);

        const request: GetCheckRequest = {
            user_id: userState.state.userId!!,
            use_claimed_amounts: true,
            release_claims: releaseClaims,
        };        

        let url: string | undefined = process.env.REACT_APP_CHECKOUT_API_URL + "/location/" + checkOutState.state.restaurantId + "/checks/" + checkSummary.id

        const headers = {
            'X-API-Key': checkOutState.state.restaurantApiKey,
            'location_id': checkOutState.state.restaurantId
        }

        axios.post(url, request, { headers, cancelToken: cancelTokenSource.token })
        .then(function(response) {
            let r = response.data as GetCheckResponse;

            if (r.success === true) {
                if (r.summary.check_state !== CheckStateEnum.Paid) {
                    if (r.summary.unclaimed_amount === 0) {
                        setNoUnclaimedAmountsError(true);
                    }
                    else {
                        setNoUnclaimedAmountsError(false);
                    }

                    const newCheckState: CheckState = checkState.state;
                    newCheckState.checkSummary = r.summary;
                    newCheckState.checkDetail = r.detail;
                    newCheckState.splitAmount = 0;
                    newCheckState.splitType = SplitType.None;
                    newCheckState.splitEvenlyCount = 0;
                    newCheckState.splitPercentage = 0;
                    newCheckState.paymentId = null;

                    checkState.setState(newCheckState);

                    if (checkOutState.state.feedbackType === FeedbackType.Akira) {
                        r.summary.disable_split = true
                    }
                    else if (checkOutState.state.restaurantName != null && checkOutState.state.restaurantName !== undefined) {
                        if (checkOutState.state.restaurantName!!.toLowerCase().indexOf("airport") != -1) {
                            r.summary.disable_split = true
                        }
                    }
        
                    setCheckSummary(r.summary);
                    setCheck(r.detail);
                }
                else {
                    setNoUnclaimedAmountsError(true);
                    setCheckPaidError(true);
                    setErrorMessage(String_CheckPaid.find(s => s.key === userState.state.locale)!!.value);
                    setErrorDialogTryAgain(false);
                    setErrorDialogOpen(true);
                }
            }
            else {
                setErrorMessage(String_System_GeneralError.find(s => s.key === userState.state.locale)!!.value);
                setErrorDialogTryAgain(true);
                setErrorDialogOpen(true);
                setLoadError(true);
            }

            setLoaded(true);
            setIsRefreshing(false); 
        })
        .catch(function(error) {
            if (error.response) {
                setErrorMessage(String_System_GeneralError.find(s => s.key === userState.state.locale)!!.value);
            }
            else {
                setErrorMessage(String_System_ConnectionError.find(s => s.key === userState.state.locale)!!.value);
            }
            
            setErrorDialogTryAgain(true);
            setErrorDialogOpen(true);
            setLoaded(true);
            setIsRefreshing(false); 
            setLoadError(true);
        });
    };

    const loadDemoData = () => {
        var checkFlat: CheckFlat = loadDemoCheckData()

        const newCheckState: CheckState = checkState.state;
        newCheckState.checkSummary = checkSummary;
        newCheckState.checkDetail = checkFlat;
        newCheckState.splitAmount = 0;
        newCheckState.splitType = SplitType.None;
        newCheckState.splitEvenlyCount = 0;
        newCheckState.splitPercentage = 0;
        newCheckState.paymentId = null;

        checkState.setState(newCheckState);

        setCheck(checkFlat);

        setLoaded(true);
    }

    const handleCheckOutLeftButtonClick = (event: React.MouseEvent<HTMLElement>) => {
        history.push('/checkout/split');
    }

    const handleCheckOutRightButtonClick = (event: React.MouseEvent<HTMLElement>) => {
        history.push('/payment/pay');
    }

    const splitButtonProps: SplitButtonProps = {
        leftLabel: String_Check_SplitButton.find(s => s.key === userState.state.locale)!!.value,
        rightLabel: String_Check_PayCheckButton.find(s => s.key === userState.state.locale)!!.value,
        leftDisabled: noUnclaimedAmountsError || checkSummary.disable_split,
        rightDisabled: noUnclaimedAmountsError,
        showIcons: false,
        useForwardIcon: false,
        useBackIcon: false,
        onLeftClickHandler: handleCheckOutLeftButtonClick,
        onRightClickHandler: handleCheckOutRightButtonClick,
    }

    const isSplitBySeatLockedButtonProps: FullWidthButtonProps = {
        disabled: false,
        label: String_Check_SplitButton.find(s => s.key === userState.state.locale)!!.value,
        alternateColor: false,
        size: FullWidthButtonSize.Large,
        onClickHandler: handleCheckOutLeftButtonClick,
    }

    const fullWidthButtonProps: FullWidthButtonProps = {
        disabled: false,
        label: String_Check_PayCheckButton.find(s => s.key === userState.state.locale)!!.value,
        alternateColor: false,
        size: FullWidthButtonSize.Large,
        onClickHandler: handleCheckOutRightButtonClick,
    }

    const handleBackButtonClick = (event: React.MouseEvent<HTMLElement>) => {
        history.goBack()
        /*if (checkOutState.state.checks !== null) {
            if (checkOutState.state.checks.length === 1) {

            }
            else { history.push({
                pathname: '/checkout/multiplechecks',
                state: { 
                    checks: checkOutState.state.checks,
                }
            }); 
    
            }
        } */       
    }

    const handleRefreshButtonClick = (event: React.MouseEvent<HTMLElement>) => {
        if (!isRefreshing && checkOutState.state.checkOutType !== CheckOutType.Demo)
        {
            setIsRefreshing(true);
            loadData(axios.CancelToken.source(), true);
        }
    }

    const handleErrorDialogClose = (event: React.MouseEvent<HTMLElement>) => {
        if (loadError) {
            setErrorDialogOpen(false);
            loadData(axios.CancelToken.source(), true);
        }
        else if (checkPaidError) {
            history.goBack();
        }
        else {
            setErrorDialogOpen(false);
        }
    }

    const errorDialogProps: ErrorDialogProps = {
        open: errorDialogOpen,
        message: errorMessage,
        tryAgain: errorDialogTryAgain,
        onCloseHandler: handleErrorDialogClose,
    }

    const signalRInitializedHandler = () => {
        
    }

    const truncateCheckNumber = (): String => {
        /*if (checkSummary.number.length > 7) {
            let index = checkSummary.number.indexOf("-")
            if (index !== -1) {
                return checkSummary.number.substring(0, index)
            }
            else {
                return checkSummary.number.substring(0, 6)
            }
        }
        else {
            return checkSummary.number
        }*/
        return checkSummary.number
    }

    return (
        <div className={clsx(classes.root, {
            [classes.rootLandscape]: !isPortrait,
        })}>

            {!loaded && !loadError &&
                <div className={classes.loadingContainer}>
                    {!loaded &&
                        <Loader />                    
                    }
                </div>
            }

            <div className={isPortrait === true ? classes.details : classes.detailsLandscape} ref={detailsEl}>
                {loaded && !loadError && check !== null && check !== undefined && 
                    checkOutState.state.checkOutType !== CheckOutType.ScanToPay &&
                    checkOutState.state.checkOutType !== CheckOutType.ScanReceiptToPay &&
                    checkOutState.state.checkOutType !== CheckOutType.Demo &&
                    <div>
                        <ShareLink />
                    </div>
                }

                {loaded && !loadError && check !== null && check !== undefined &&
                    <div className={classes.titleInnerContainer}>
                        <div className={classes.titleBackContainer}>
                            {checkOutState.state.checkOutType !== CheckOutType.ScanReceiptToPay &&
                                <BackButton disabled={false} hidden={false} onClickHandler={handleBackButtonClick}/>
                            }
                            {checkOutState.state.checkOutType === CheckOutType.ScanReceiptToPay &&
                                <BackButton disabled={false} hidden={true} onClickHandler={handleBackButtonClick}/>
                            }
                        </div>
                        <div className={classes.titleTextContainer}>
                            <Typography variant="h6" className={classes.titleText}>
                                {String_Check_CheckDetails.find(s => s.key === userState.state.locale)!!.value}
                            </Typography>
                        </div>
                        <div className={classes.titleRefreshButtonContainer}>
                            <RefreshButton disabled={false} onClickHandler={handleRefreshButtonClick}/>
                        </div>
                    </div>
                }

                {loaded && !loadError && check !== null && check !== undefined &&
                    <div className={classes.checkDetailsContainer}>
                        <CheckDetails details={check.details} isReceipt={false}/>
                    </div>
                }
            </div>

            {loaded && !loadError && check !== null && check !== undefined && !checkSummary.disable_split && 
                <div className={isPortrait ? classes.footerButtons : classes.footerButtonsLandscape}>
                    <SplitButtons
                        leftLabel={splitButtonProps.leftLabel}
                        rightLabel={splitButtonProps.rightLabel}
                        leftDisabled={splitButtonProps.leftDisabled}
                        rightDisabled={splitButtonProps.rightDisabled}
                        showIcons={splitButtonProps.showIcons}
                        useForwardIcon={splitButtonProps.useForwardIcon}
                        useBackIcon={splitButtonProps.useBackIcon}
                        onLeftClickHandler={splitButtonProps.onLeftClickHandler}
                        onRightClickHandler={splitButtonProps.onRightClickHandler} />
                </div>
            }

            {loaded && !loadError && check !== null && check !== undefined && checkSummary.disable_split &&
                <div className={isPortrait ? classes.footerButtons : classes.footerButtonsLandscape}>
                     <FullWidthButton 
                        disabled={fullWidthButtonProps.disabled}
                        label={fullWidthButtonProps.label}
                        alternateColor={fullWidthButtonProps.alternateColor}
                        size={fullWidthButtonProps.size}
                        onClickHandler={fullWidthButtonProps.onClickHandler}/>
                </div>
            }

            {loaded && !loadError && check !== null && check !== undefined && checkSummary.is_split_by_seat_locked &&
                <div className={isPortrait ? classes.footerButtons : classes.footerButtonsLandscape}>
                     <FullWidthButton 
                        disabled={isSplitBySeatLockedButtonProps.disabled}
                        label={isSplitBySeatLockedButtonProps.label}
                        alternateColor={isSplitBySeatLockedButtonProps.alternateColor}
                        size={isSplitBySeatLockedButtonProps.size}
                        onClickHandler={isSplitBySeatLockedButtonProps.onClickHandler}/>
                </div>
            }

            <ErrorDialog
                open={errorDialogProps.open}
                message={errorDialogProps.message}
                tryAgain={errorDialogProps.tryAgain}
                onCloseHandler={errorDialogProps.onCloseHandler} />

            {checkOutState.state.checks !== null && 
                <SignalR 
                    oldChecks={null}
                    messageType={CheckMessageType.Subscribe}
                    load={true}
                    refreshCheck={true}
                    onNotificationReceived={signalRNotificationReceivedHandler}
                    onInitialized={signalRInitializedHandler} />
            }

        </div>
    );
};

export default Check;